// =======================================================
// „Feedback" in der Seitenleiste
//
// Auf jeder Seite erreichbar: Wer merkt, dass etwas fehlt oder hakt, schreibt
// es in ein, zwei Sätzen auf und schickt es als Ticket ins Ticketsystem.
// Ein Foto der Seite ist schon dabei, weitere Bilder gehen per Büroklammer,
// Ziehen oder Strg+V. Link, Seite, Reiter, Browser und die letzten Fehler
// hängen wir selbst an.
//
// Der Dialog lässt sich minimieren: Der Entwurf liegt im Store
// (meldungEntwurfStore), nicht im Dialog, und übersteht so jeden Seitenwechsel.
// Minimiert zeigt eine kleine Leiste, in der man weitere Seiten fotografieren
// kann.
// =======================================================
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  MessageSquarePlus, Paperclip, X, Loader2, CheckCircle2, ImageOff, FileText, Send, ExternalLink,
  Camera, Pencil, Trash2,
} from 'lucide-react';
import { effectiveRoleOf, useAuthStore } from '../../stores/authStore';
import { isSupabaseAvailable } from '../../lib/supabase';
import { anzeigeName } from '../../lib/anzeigeName';
import {
  ERLAUBTE_TYPEN, MAX_ANHAENGE, meldungAbschicken, technischeAngaben, type MeldungErgebnis,
} from '../../lib/meldung';
import {
  anzahlBilder, belegtePlaetze, entwurfIstLeer, useMeldungEntwurf,
} from '../../stores/meldungEntwurfStore';
import { ROLE_LABEL } from '../../types';
import './Melden.css';

/**
 * Vorschau-Adresse für eine Datei. Entsteht im Effekt und wird dort wieder
 * freigegeben: Der Dialog wird beim Minimieren abgebaut und beim Öffnen neu
 * aufgebaut, eine einmal freigegebene Adresse darf dann nicht weiterleben.
 */
function useObjektUrl(datei: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!datei) { setUrl(null); return; }
    const u = URL.createObjectURL(datei);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [datei]);
  return url;
}

const uhrKurz = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

export function MeldenKnopf() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const offen = useMeldungEntwurf((s) => s.offen);
  const entwurf = useMeldungEntwurf((s) => s.entwurf);
  const oeffnen = useMeldungEntwurf((s) => s.oeffnen);
  const wiederherstellen = useMeldungEntwurf((s) => s.wiederherstellen);

  // Nach dem Neuladen den gesicherten Text wieder hervorholen.
  useEffect(() => {
    if (userId) wiederherstellen(userId);
  }, [userId, wiederherstellen]);

  if (!isSupabaseAvailable) return null;
  const hatEntwurf = entwurf && !offen;

  return (
    <>
      <button
        type="button"
        className="cc-melden-knopf"
        data-tour="melden"
        onClick={oeffnen}
        title={hatEntwurf
          ? 'Du hast einen Feedback-Entwurf angefangen — hier weiterschreiben'
          : 'Fehlt hier etwas oder hakt es? Direkt als Ticket melden'}
        aria-label={hatEntwurf
          ? 'Feedback-Entwurf weiterschreiben'
          : 'Feedback geben: fehlt etwas oder hakt es?'}
      >
        <MessageSquarePlus size={16} />
        <span className="cc-melden-knopf__text">Feedback</span>
        {hatEntwurf && <span className="cc-melden-knopf__punkt" aria-hidden="true" />}
      </button>
      {offen && <MeldenDialog />}
      {hatEntwurf && <MeldenLeiste />}
    </>
  );
}

/**
 * Der Dialog hängt direkt unter <body>: Die Seitenleiste ist „sticky" und
 * bildet damit eine eigene Ebene — innerhalb davon läge der Dialog unter
 * Power-Dialer und Anruf-Fenstern. Die Hülle trägt die Markierung für den
 * Screenshot-Filter, damit der Dialog nicht auf dem Foto der Seite landet.
 */
function Dialogfenster({
  titel, breit, onClose, schliessenTitel = 'Schließen', children,
}: { titel: string; breit?: boolean; onClose: () => void; schliessenTitel?: string; children: ReactNode }) {
  const titelId = useId();
  const fensterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fokus nur beim Öffnen in den Dialog — hat ein Feld (autoFocus) ihn schon,
  // bleibt er dort.
  useEffect(() => {
    const el = fensterRef.current;
    if (el && !el.contains(document.activeElement)) el.focus();
  }, []);

  return createPortal(
    <div className="melden-huelle" data-melden-ignorieren>
      <div className="melden-hintergrund" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div
          ref={fensterRef}
          className={`melden-fenster${breit ? ' melden-fenster--breit' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titelId}
          tabIndex={-1}
        >
          <div className="melden-kopf">
            <h2 className="melden-titel" id={titelId}>{titel}</h2>
            <button type="button" className="melden-zu" onClick={onClose} title={schliessenTitel} aria-label={schliessenTitel}>
              <X size={18} />
            </button>
          </div>
          <div className="melden-inhalt">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function MeldenDialog() {
  const profile = useAuthStore((s) => s.profile);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const viewAsRole = useAuthStore((s) => s.viewAsRole);

  const kontext = useMeldungEntwurf((s) => s.kontext);
  const beschreibung = useMeldungEntwurf((s) => s.beschreibung);
  const screenshot = useMeldungEntwurf((s) => s.screenshot);
  const screenshotStand = useMeldungEntwurf((s) => s.fotoStand);
  const screenshotMit = useMeldungEntwurf((s) => s.fotoMit);
  const dateien = useMeldungEntwurf((s) => s.dateien);
  const weitereSeiten = useMeldungEntwurf((s) => s.weitereSeiten);
  const aktionen = useMeldungEntwurf.getState;

  const [dateiFehler, setDateiFehler] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<MeldungErgebnis | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const [verwerfenFrage, setVerwerfenFrage] = useState(false);
  const dateiInput = useRef<HTMLInputElement>(null);

  // Foto sofort beim ersten Öffnen — der Dialog selbst ist ausgenommen. Beim
  // erneuten Öffnen eines minimierten Entwurfs läuft nichts Neues an.
  useEffect(() => {
    if (useMeldungEntwurf.getState().fotoStand === 'laeuft') void aktionen().ersteFotoStarten();
  }, [aktionen]);

  const screenshotUrl = useObjektUrl(screenshot);

  const email = profile?.email ?? user?.email ?? '';
  const effektiv = effectiveRoleOf({ role, viewAsRole });
  const anzeige = anzeigeName(profile?.first_name, profile?.last_name, email);
  const melder = {
    name: anzeige === '—' ? '' : anzeige,
    email,
    // Die echte Rolle; läuft gerade „Ansicht als …", steht die Vorschau extra dabei.
    rolle: role ? ROLE_LABEL[role] : '',
    ansichtAls: role && effektiv && effektiv !== role ? ROLE_LABEL[effektiv] : null,
  };
  const mitScreenshot = screenshotMit && !!screenshot;
  const platz = MAX_ANHAENGE - (belegtePlaetze({ screenshot, fotoStand: screenshotStand, fotoMit: screenshotMit, dateien }) - dateien.length);
  const hatInhalt = !entwurfIstLeer({ beschreibung, dateien, weitereSeiten });

  const hinzufuegen = (neu: File[]) => {
    if (neu.length === 0) return;
    setDateiFehler(aktionen().hinzufuegen(neu));
  };

  const beimEinfuegen = (e: React.ClipboardEvent) => {
    const bilder = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (bilder.length === 0) return;
    e.preventDefault();
    // Eingefügte Bilder heißen im Browser alle „image.png" — durchnummerieren.
    hinzufuegen(bilder.map((f, i) => new File([f], `eingefuegt-${Date.now()}-${i + 1}.png`, { type: f.type })));
  };

  const absenden = async () => {
    if (!kontext) return;
    if (beschreibung.trim().length < 10) {
      setFehler('Bitte beschreib kurz, was fehlt oder hakt — ein, zwei Sätze reichen.');
      return;
    }
    setFehler('');
    setSendet(true);
    try {
      const anhaenge = [...(mitScreenshot && screenshot ? [screenshot] : []), ...dateien];
      const e = await meldungAbschicken({ beschreibung, kontext, melder, anhaenge, mitScreenshot, weitereSeiten });
      // Erst die Danke-Ansicht merken, dann den Entwurf und den gesicherten
      // Text leeren — der Dialog darf dazwischen nicht ohne Inhalt dastehen.
      setErgebnis(e);
      aktionen().gesendet();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setSendet(false);
    }
  };

  const schliessen = () => aktionen().schliessen();

  if (ergebnis) {
    return (
      <Dialogfenster titel="Danke für deine Meldung" onClose={schliessen}>
        <div className="melden-danke">
          <CheckCircle2 size={44} className="melden-danke-icon" aria-hidden="true" />
          <p>Dein Ticket ist angelegt:</p>
          <p className="melden-nummer">{ergebnis.nummer}</p>
          <p className="melden-hinweis">
            Du bekommst eine Bestätigung per Mail. Den Stand und Rückfragen siehst du unter
            „Meine Tickets" im Ticketsystem.
          </p>
          {ergebnis.anhaengeFehlgeschlagen > 0 && (
            <p className="melden-warnung">
              {ergebnis.anhaengeFehlgeschlagen === 1 ? 'Ein Anhang' : `${ergebnis.anhaengeFehlgeschlagen} Anhänge`}{' '}
              ließ sich nicht hochladen. Du kannst ihn unter „Meine Tickets" nachreichen.
            </p>
          )}
        </div>
        <div className="melden-aktionen">
          {ergebnis.meineTicketsUrl && (
            <a className="melden-btn melden-btn--sekundaer" href={ergebnis.meineTicketsUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={15} /> Meine Tickets
            </a>
          )}
          <button type="button" className="melden-btn melden-btn--primaer" onClick={schliessen} autoFocus>Schließen</button>
        </div>
      </Dialogfenster>
    );
  }

  if (!kontext) return null;

  return (
    <Dialogfenster
      titel="Fehlt hier etwas?"
      breit
      // X, Escape und Klick daneben minimieren — der Entwurf bleibt.
      onClose={() => { if (!sendet) schliessen(); }}
      schliessenTitel="Minimieren — dein Entwurf bleibt erhalten"
    >
      <div
        className={`melden${ziehen ? ' melden-ziehen' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
        onDragLeave={() => setZiehen(false)}
        onDrop={(e) => { e.preventDefault(); setZiehen(false); hinzufuegen(Array.from(e.dataTransfer.files)); }}
      >
        <div className="melden-ort">
          <p className="melden-ort-zeile">
            <span className="melden-ort-label">Seite</span> {kontext.seite}
          </p>
          {weitereSeiten.length > 0 && (
            <>
              <p className="melden-ort-zeile melden-ort-weitere">
                <span className="melden-ort-label">Weitere Seiten</span> (fotografiert)
              </p>
              <ol className="melden-ort-liste">
                {weitereSeiten.map((w) => (
                  <li key={w.dateiName}>{w.seite} <span className="melden-ort-zeit">· {uhrKurz.format(w.zeitpunkt)} Uhr</span></li>
                ))}
              </ol>
            </>
          )}
        </div>

        <div className="melden-gruppe">
          <label className="melden-label" htmlFor="melden-text">Was fehlt oder hakt?</label>
          <textarea
            id="melden-text"
            className="melden-textarea"
            value={beschreibung}
            onChange={(e) => aktionen().setBeschreibung(e.target.value)}
            onPaste={beimEinfuegen}
            placeholder={'z. B. In der Anrufliste wäre ein Filter „Nur Rückrufwünsche" gut — neben der Auswahl nach Standort.'}
            rows={5}
            maxLength={5000}
            autoFocus
            disabled={sendet}
          />
          <p className="melden-hinweis">
            Ein, zwei Sätze reichen. Link, Seite und technische Angaben hängen wir selbst an.
            Mit dem Minimieren (X) bleibt dein Entwurf erhalten.
          </p>
        </div>

        <div className="melden-gruppe">
          <span className="melden-label">Bilder</span>
          <div className="melden-anhaenge">
            {screenshotStand === 'laeuft' && (
              <div className="melden-kachel melden-kachel-laedt">
                <Loader2 size={18} className="melden-dreht" aria-hidden="true" />
                <span>Foto der Seite …</span>
              </div>
            )}
            {screenshotStand === 'fertig' && screenshotUrl && (
              <div className={`melden-kachel melden-kachel-bild${screenshotMit ? '' : ' aus'}`}>
                <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" title="Groß ansehen">
                  <img src={screenshotUrl} alt="Foto der aktuellen Seite" />
                </a>
                <span className="melden-kachel-name">{screenshotMit ? 'Foto dieser Seite' : 'Foto weggelassen'}</span>
                <button
                  type="button"
                  className="melden-kachel-weg"
                  onClick={() => aktionen().fotoUmschalten()}
                  title={screenshotMit ? 'Foto nicht mitschicken' : 'Foto doch mitschicken'}
                  aria-label={screenshotMit ? 'Foto nicht mitschicken' : 'Foto doch mitschicken'}
                  disabled={sendet}
                >
                  {screenshotMit ? <X size={14} /> : <ImageOff size={14} />}
                </button>
              </div>
            )}
            {dateien.map((f, i) => (
              <DateiKachel
                key={`${f.name}-${i}`}
                datei={f}
                onWeg={() => aktionen().dateiEntfernen(i)}
                gesperrt={sendet}
              />
            ))}
            {dateien.length < platz && (
              <>
                <button
                  type="button"
                  className="melden-kachel melden-kachel-neu"
                  onClick={() => dateiInput.current?.click()}
                  disabled={sendet}
                >
                  <Paperclip size={18} aria-hidden="true" />
                  <span>Bild anhängen</span>
                </button>
                <button
                  type="button"
                  className="melden-kachel melden-kachel-neu"
                  onClick={() => aktionen().minimieren()}
                  disabled={sendet}
                  title="Dialog minimieren, zur gewünschten Seite wechseln und dort in der Leiste „Screenshot dieser Seite“ wählen"
                >
                  <Camera size={18} aria-hidden="true" />
                  <span>Andere Seite fotografieren</span>
                </button>
              </>
            )}
          </div>
          <input
            ref={dateiInput}
            type="file"
            accept={ERLAUBTE_TYPEN}
            multiple
            hidden
            onChange={(e) => { hinzufuegen(Array.from(e.target.files ?? [])); e.target.value = ''; }}
          />
          <p className="melden-hinweis">
            {screenshotStand === 'fehlt'
              ? 'Das automatische Foto hat nicht geklappt — häng gern selbst einen Screenshot an. '
              : ''}
            Auch per Ziehen oder Strg+V ins Textfeld. Bilder und PDF, bis 10 MB.
            „Andere Seite fotografieren“ minimiert den Dialog, damit du erst dorthin wechseln kannst.
          </p>
          {dateiFehler && <p className="melden-warnung">{dateiFehler}</p>}
        </div>

        <details className="melden-technik">
          <summary>Was automatisch mitgeht</summary>
          <pre>{technischeAngaben(kontext, melder, mitScreenshot, weitereSeiten)}</pre>
        </details>

        {fehler && <p className="melden-fehler" role="alert">{fehler}</p>}

        {verwerfenFrage ? (
          <div className="melden-aktionen" role="alertdialog" aria-label="Entwurf verwerfen?">
            <span className="melden-frage">Entwurf mit Text und Bildern wirklich verwerfen?</span>
            <button type="button" className="melden-btn melden-btn--sekundaer" onClick={() => setVerwerfenFrage(false)}>
              Zurück zum Entwurf
            </button>
            <button type="button" className="melden-btn melden-btn--gefahr" onClick={() => aktionen().verwerfen()} autoFocus>
              <Trash2 size={15} /> Ja, verwerfen
            </button>
          </div>
        ) : (
          <div className="melden-aktionen">
            <button
              type="button"
              className="melden-btn melden-btn--sekundaer"
              onClick={() => { if (hatInhalt) setVerwerfenFrage(true); else aktionen().verwerfen(); }}
              disabled={sendet}
            >
              Entwurf verwerfen
            </button>
            <button
              type="button"
              className="melden-btn melden-btn--primaer"
              onClick={() => void absenden()}
              disabled={sendet || screenshotStand === 'laeuft'}
              title={screenshotStand === 'laeuft' ? 'Einen Moment — das Foto der Seite entsteht noch' : undefined}
            >
              {sendet ? <Loader2 size={15} className="melden-dreht" /> : <Send size={15} />}
              {sendet ? 'Wird gesendet …' : 'Als Ticket senden'}
            </button>
          </div>
        )}
      </div>
    </Dialogfenster>
  );
}

/**
 * Minimierter Entwurf: eine kleine Leiste, die über allem anderen steht. Von
 * hier aus lässt sich weiterschreiben, die gerade offene Seite fotografieren
 * oder der Entwurf verwerfen. Die Leiste trägt die Markierung für den
 * Screenshot-Filter und landet deshalb nie im Foto.
 */
function MeldenLeiste() {
  const oeffnen = useMeldungEntwurf((s) => s.oeffnen);
  const fotoLaeuft = useMeldungEntwurf((s) => s.fotoLaeuft);
  const hinweis = useMeldungEntwurf((s) => s.hinweis);
  const screenshot = useMeldungEntwurf((s) => s.screenshot);
  const fotoMit = useMeldungEntwurf((s) => s.fotoMit);
  const dateien = useMeldungEntwurf((s) => s.dateien);
  const beschreibung = useMeldungEntwurf((s) => s.beschreibung);
  const weitereSeiten = useMeldungEntwurf((s) => s.weitereSeiten);
  const [frage, setFrage] = useState(false);

  const n = anzahlBilder({ screenshot, fotoMit, dateien });
  const hatInhalt = !entwurfIstLeer({ beschreibung, dateien, weitereSeiten });
  const aktionen = useMeldungEntwurf.getState;

  return createPortal(
    <div className="melden-huelle" data-melden-ignorieren>
      <div className="melden-leiste" role="region" aria-label="Feedback-Entwurf, minimiert">
        <div className="melden-leiste-kopf">
          <MessageSquarePlus size={16} aria-hidden="true" />
          <strong>Feedback-Entwurf</strong>
          <span className="melden-leiste-zahl">{n} {n === 1 ? 'Bild' : 'Bilder'}</span>
        </div>
        {frage ? (
          <div className="melden-leiste-aktionen">
            <span className="melden-leiste-frage">Entwurf wirklich verwerfen?</span>
            <button type="button" className="melden-btn melden-btn--sekundaer melden-btn--leiste" onClick={() => setFrage(false)}>
              Behalten
            </button>
            <button type="button" className="melden-btn melden-btn--gefahr melden-btn--leiste" onClick={() => aktionen().verwerfen()} autoFocus>
              <Trash2 size={15} /> Verwerfen
            </button>
          </div>
        ) : (
          <div className="melden-leiste-aktionen">
            <button type="button" className="melden-btn melden-btn--primaer melden-btn--leiste" onClick={oeffnen}>
              <Pencil size={15} /> Weiter schreiben
            </button>
            <button
              type="button"
              className="melden-btn melden-btn--sekundaer melden-btn--leiste"
              onClick={() => void aktionen().weitereSeiteAufnehmen()}
              disabled={fotoLaeuft}
            >
              {fotoLaeuft ? <Loader2 size={15} className="melden-dreht" /> : <Camera size={15} />} Screenshot dieser Seite
            </button>
            <button
              type="button"
              className="melden-btn melden-btn--sekundaer melden-btn--leiste"
              onClick={() => { if (hatInhalt) setFrage(true); else aktionen().verwerfen(); }}
            >
              <Trash2 size={15} /> Verwerfen
            </button>
          </div>
        )}
        <p className="melden-leiste-hinweis" role="status" aria-live="polite">{hinweis}</p>
      </div>
    </div>,
    document.body,
  );
}

function DateiKachel({ datei, onWeg, gesperrt }: { datei: File; onWeg: () => void; gesperrt: boolean }) {
  const istBild = datei.type.startsWith('image/') && !/hei[cf]/.test(datei.type);
  const url = useObjektUrl(istBild ? datei : null);

  return (
    <div className="melden-kachel melden-kachel-bild">
      {url ? <img src={url} alt="" /> : <FileText size={22} className="melden-kachel-datei" aria-hidden="true" />}
      <span className="melden-kachel-name" title={datei.name}>{datei.name}</span>
      <button
        type="button"
        className="melden-kachel-weg"
        onClick={onWeg}
        title="Entfernen"
        aria-label={`${datei.name} entfernen`}
        disabled={gesperrt}
      >
        <X size={14} />
      </button>
    </div>
  );
}
